import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/apiClient";

const SpeechRecognition = typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition);
const synth = typeof window !== "undefined" ? window.speechSynthesis : null;
// Tiny silent WAV used to unlock HTMLMediaElement playback on mobile (first user gesture).
const SILENT_WAV = "data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQAAAAA=";
const isMobile = typeof navigator !== "undefined" && /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent || "");
const BACKEND = process.env.REACT_APP_BACKEND_URL;
// Mobile recognizers emit isFinal per fragment (often a single word): buffer them and
// only emit the full utterance after this silence window. Kept short for tight turn-taking
// (Sara replies right after you stop talking); user-adjustable 0.3s–2.0s via settings.
const SILENCE_MS = isMobile ? 600 : 400;

// Merge a new recognition fragment into an accumulator WITHOUT producing the cascading
// duplication some mobile engines cause (they re-deliver growing/cumulative results, e.g.
// "je" → "je veux" → "je veux que" ...). We collapse prefixes/overlaps/corrections and only
// append when the fragment is genuinely new text.
function mergeTranscript(acc, frag) {
  const t = (frag || "").trim();
  if (!t) return acc;
  if (!acc) return t;
  const al = acc.toLowerCase();
  const fl = t.toLowerCase();
  if (fl.startsWith(al)) return t;          // fragment extends the accumulator → keep fragment
  if (al.startsWith(fl)) return acc;        // fragment is a prefix already covered → keep acc
  if (al.includes(fl)) return acc;          // fragment already contained
  if (fl.includes(al)) return t;            // fragment is a superset
  // long shared prefix ⇒ a mid-phrase correction/re-delivery → keep the longer version
  let n = 0;
  const m = Math.min(al.length, fl.length);
  while (n < m && al[n] === fl[n]) n++;
  if (m > 0 && n >= m * 0.6) return t.length >= acc.length ? t : acc;
  return acc + " " + t;                     // genuinely new speech → append
}

const FEMALE_HINTS = [
  "amelie", "amélie", "audrey", "marie", "julie", "virginie", "hortense", "celine", "céline",
  "lea", "léa", "google français", "female", "femme", "samantha", "victoria", "aria", "jenny", "sara",
];
function pickFemaleVoice(all) {
  if (!all || !all.length) return null;
  const fr = all.filter((v) => (v.lang || "").toLowerCase().startsWith("fr"));
  const en = all.filter((v) => (v.lang || "").toLowerCase().startsWith("en"));
  const pool = fr.length ? fr : en.length ? en : all;
  return pool.find((v) => FEMALE_HINTS.some((h) => v.name.toLowerCase().includes(h))) || pool[0];
}

export function useSpeech() {
  const recognitionRef = useRef(null);
  const wantListeningRef = useRef(false);
  const onResultRef = useRef(null);
  const listenModeRef = useRef(null);      // "browser" | null
  const restartTimerRef = useRef(null);
  const finalBufRef = useRef("");
  const pendingTextRef = useRef("");
  const flushTimerRef = useRef(null);
  const silenceMsRef = useRef(SILENCE_MS);
  const wakeLockRef = useRef(null);
  const recogLangRef = useRef("fr-FR");
  const mediaRecRef = useRef(null);
  const whisperChunksRef = useRef([]);
  // --- continuous-recognition health detection (Brave / blocked speech backends) ---
  const gotResultEverRef = useRef(false);   // true once the engine ever returned a result
  const emptyRestartCountRef = useRef(0);    // consecutive empty start→end cycles / errors
  const lastEndTsRef = useRef(0);

  const audioCtxRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(null);
  const micLevelRef = useRef(0);
  const audioElRef = useRef(null);
  const speakLevelRef = useRef(0);
  const speakRafRef = useRef(null);
  const speakSourceMapRef = useRef(new WeakMap());

  const [voices, setVoices] = useState([]);
  const [listening, setListening] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [interim, setInterim] = useState("");
  const [micReady, setMicReady] = useState(false);
  const [whisperRecording, setWhisperRecording] = useState(false);
  // true when continuous browser recognition is unusable on THIS device (so the UI
  // falls back to push-to-talk only here, instead of forcing it on every mobile).
  const [recognitionBroken, setRecognitionBroken] = useState(false);

  const recogSupported = Boolean(SpeechRecognition);
  const ttsSupported = Boolean(synth);

  useEffect(() => {
    if (!synth) return;
    const load = () => setVoices(synth.getVoices());
    load();
    synth.addEventListener("voiceschanged", load);
    return () => synth.removeEventListener("voiceschanged", load);
  }, []);

  // ---- persistent microphone level meter (used for the HUD + barge-in) ----
  const enableMic = useCallback(async () => {
    // Always resume the audio context (needed for the TTS playback graph).
    try {
      const ctx = audioCtxRef.current || new (window.AudioContext || window.webkitAudioContext)();
      audioCtxRef.current = ctx;
      if (ctx.state === "suspended") await ctx.resume();
    } catch (_) {}

    // MOBILE: a persistent getUserMedia stream conflicts with SpeechRecognition on Android
    // (recognition aborts after ~1s). Confirm mic permission then release it immediately so
    // SpeechRecognition can own the microphone.
    if (isMobile) {
      try {
        const s = await navigator.mediaDevices.getUserMedia({ audio: true });
        s.getTracks().forEach((t) => t.stop());
        setMicReady(true);
        return true;
      } catch (_) {
        setMicReady(false);
        return false;
      }
    }

    // DESKTOP: keep a persistent stream to drive the mic-level meter + barge-in.
    if (streamRef.current) return true;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
      streamRef.current = stream;
      const ctx = audioCtxRef.current;
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      const data = new Uint8Array(analyser.frequencyBinCount);
      const tick = () => {
        analyser.getByteFrequencyData(data);
        let sum = 0;
        for (let i = 0; i < data.length; i++) sum += data[i];
        micLevelRef.current = Math.min(1, sum / data.length / 255 * 2.4);
        rafRef.current = requestAnimationFrame(tick);
      };
      tick();
      setMicReady(true);
      return true;
    } catch (e) {
      setMicReady(false);
      return false;
    }
  }, []);

  const disableMic = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    micLevelRef.current = 0;
    setMicReady(false);
  }, []);

  // ---- screen wake lock: keep the PWA awake so recognition isn't suspended while the app is open ----
  const requestWakeLock = useCallback(async () => {
    try {
      if ("wakeLock" in navigator && document.visibilityState === "visible" && !wakeLockRef.current) {
        wakeLockRef.current = await navigator.wakeLock.request("screen");
        wakeLockRef.current.addEventListener &&
          wakeLockRef.current.addEventListener("release", () => { wakeLockRef.current = null; });
      }
    } catch (_) {
      wakeLockRef.current = null;
    }
  }, []);

  const releaseWakeLock = useCallback(() => {
    try {
      wakeLockRef.current && wakeLockRef.current.release && wakeLockRef.current.release();
    } catch (_) {}
    wakeLockRef.current = null;
  }, []);

  // ---- continuous speech recognition ----
  const flushFinal = useCallback(() => {
    if (flushTimerRef.current) clearTimeout(flushTimerRef.current);
    flushTimerRef.current = null;
    const text = (pendingTextRef.current || finalBufRef.current || "").replace(/\s+/g, " ").trim();
    pendingTextRef.current = "";
    finalBufRef.current = "";
    setInterim("");
    if (!text) return;
    onResultRef.current && onResultRef.current({ final: text, interim: "" });
    // Reset the recognition session so already-finalized results are NOT re-processed
    // on the next event — root cause of the repeated/cascading transcript on mobile PWAs
    // (e.g. "je / je veux / je veux que ..."). onend auto-restarts a fresh session.
    try { recognitionRef.current && recognitionRef.current.stop(); } catch (_) {}
  }, []);

  const startRecognition = useCallback((onResult) => {
    if (!SpeechRecognition) return false;
    onResultRef.current = onResult;
    if (wantListeningRef.current && recognitionRef.current) return true; // already running
    wantListeningRef.current = true;
    requestWakeLock();

    const recog = new SpeechRecognition();
    recog.lang = recogLangRef.current || "fr-FR";
    recog.interimResults = true;
    recog.continuous = true;
    recog.maxAlternatives = 1;

    recog.onresult = (event) => {
      // The engine is proven healthy the moment it returns anything.
      gotResultEverRef.current = true;
      emptyRestartCountRef.current = 0;
      // Rebuild the transcript from the FULL results list every event (idempotent):
      // some mobile recognizers (Brave/Android, MIUI) re-deliver growing results, so an
      // incremental "+=" accumulation produced a cascading duplicated transcript.
      let finalText = "";
      let interimText = "";
      for (let i = 0; i < event.results.length; i++) {
        const res = event.results[i];
        const t = (res[0] && res[0].transcript) || "";
        if (res.isFinal) finalText = mergeTranscript(finalText, t);
        else interimText = mergeTranscript(interimText, t);
      }
      finalBufRef.current = finalText;
      const combined = mergeTranscript(finalText, interimText);
      pendingTextRef.current = combined;
      setInterim(combined);
      onResultRef.current && onResultRef.current({ final: "", interim: combined });
      // Any new fragment (final or interim) extends the silence window.
      if (flushTimerRef.current) clearTimeout(flushTimerRef.current);
      flushTimerRef.current = setTimeout(flushFinal, silenceMsRef.current);
    };
    recog.onerror = (e) => {
      const err = (e && e.error) || "";
      if (err === "not-allowed") {
        // Mic permission denied → stop the restart loop and tell the user how to fix it.
        wantListeningRef.current = false;
        setListening(false);
        toast.error("Micro bloqué. Autorise le microphone pour Sara dans les réglages du site / de la PWA.");
        return;
      }
      // Brave / privacy browsers block the speech backend → 'network' or
      // 'service-not-allowed' with no results ever. Flag the engine as broken so the
      // UI falls back to push-to-talk ON THIS DEVICE ONLY.
      if ((err === "network" || err === "service-not-allowed") && !gotResultEverRef.current) {
        emptyRestartCountRef.current += 1;
        if (emptyRestartCountRef.current >= 2) {
          wantListeningRef.current = false;
          setListening(false);
          setRecognitionBroken(true);
        }
      }
      // no-speech / aborted / audio-capture → onend restarts if still wanted
    };
    recog.onend = () => {
      recognitionRef.current = null;
      setListening(false);
      // Android ends the session after the utterance: deliver whatever was buffered.
      if (wantListeningRef.current && (pendingTextRef.current.trim() || finalBufRef.current.trim())) flushFinal();
      // Broken engine: continuous recognition that starts then immediately ends without
      // ever producing a result (typical of Brave / blocked speech services). A healthy
      // desktop/Android recognizer never rapid-cycles (<1.5s) before the first result.
      if (wantListeningRef.current && !gotResultEverRef.current) {
        const now = Date.now();
        if (now - lastEndTsRef.current < 1500) emptyRestartCountRef.current += 1;
        else emptyRestartCountRef.current = 1;
        lastEndTsRef.current = now;
        if (emptyRestartCountRef.current >= 4) {
          wantListeningRef.current = false;
          setRecognitionBroken(true);
          return;
        }
      }
      // auto-restart to stay hands-free unless we were asked to stop.
      // While the app is backgrounded, browsers suspend recognition — don't loop here;
      // the visibilitychange handler restarts it when the app returns to the foreground.
      const visible = typeof document === "undefined" || document.visibilityState === "visible";
      if (wantListeningRef.current && visible) {
        restartTimerRef.current = setTimeout(() => {
          if (wantListeningRef.current) startRecognition(onResultRef.current);
        }, isMobile ? 450 : 250);
      }
    };

    recognitionRef.current = recog;
    setListening(true);
    try {
      recog.start();
    } catch (_) {
      // start() throws if a previous session hasn't fully ended — reset and retry shortly
      recognitionRef.current = null;
      setListening(false);
      if (wantListeningRef.current) {
        restartTimerRef.current = setTimeout(() => {
          if (wantListeningRef.current) startRecognition(onResultRef.current);
        }, isMobile ? 450 : 250);
      }
    }
    return true;
  }, [flushFinal, requestWakeLock]);

  const stopRecognition = useCallback(() => {
    wantListeningRef.current = false;
    releaseWakeLock();
    if (restartTimerRef.current) clearTimeout(restartTimerRef.current);
    if (flushTimerRef.current) clearTimeout(flushTimerRef.current);
    flushTimerRef.current = null;
    finalBufRef.current = "";
    pendingTextRef.current = "";
    setInterim("");
    try {
      recognitionRef.current && recognitionRef.current.stop();
    } catch (_) {}
    recognitionRef.current = null;
    setListening(false);
  }, [releaseWakeLock]);

  // ---- continuous listening: native browser recognizer (no ElevenLabs Scribe). ----
  const stopListening = useCallback(() => {
    listenModeRef.current = null;
    stopRecognition();
    setListening(false);
  }, [stopRecognition]);

  const startListening = useCallback(async (onResult) => {
    onResultRef.current = onResult;
    if (listenModeRef.current) return true; // already listening
    listenModeRef.current = "browser";
    startRecognition(onResultRef.current);
    return true;
  }, [startRecognition]);

  // ---- recognition language (browser recognizer handles one language at a time) ----
  const setRecogLang = useCallback((lang) => {
    recogLangRef.current = lang || "fr-FR";
    // if currently listening, stop so onend auto-restarts with the new language
    if (wantListeningRef.current) {
      try { recognitionRef.current && recognitionRef.current.stop(); } catch (_) {}
    }
  }, []);

  // ---- adjustable silence window before auto-send (0.3s – 2.0s) ----
  const setSilenceMs = useCallback((ms) => {
    const n = Number(ms);
    if (!Number.isNaN(n)) silenceMsRef.current = Math.max(300, Math.min(2000, n));
  }, []);

  // ---- Whisper (OpenAI) capture with AUTOMATIC language detection ----
  const startWhisper = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      whisperChunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data && e.data.size) whisperChunksRef.current.push(e.data); };
      mr.start();
      mediaRecRef.current = { mr, stream };
      setWhisperRecording(true);
      return true;
    } catch (_) {
      toast.error("Micro indisponible pour l'enregistrement");
      return false;
    }
  }, []);

  const stopWhisper = useCallback(async () => {
    const cur = mediaRecRef.current;
    mediaRecRef.current = null;
    setWhisperRecording(false);
    if (!cur) return null;
    const { mr, stream } = cur;
    const blob = await new Promise((resolve) => {
      mr.onstop = () => resolve(new Blob(whisperChunksRef.current, { type: mr.mimeType || "audio/webm" }));
      try { mr.stop(); } catch (_) { resolve(null); }
    });
    try { stream.getTracks().forEach((t) => t.stop()); } catch (_) {}
    if (!blob || !blob.size) return null;
    const fd = new FormData();
    fd.append("file", blob, "audio.webm");
    try {
      const { data } = await api.post("/stt/transcribe", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return data; // { text, language, language_code }
    } catch (_) {
      toast.error("Transcription échouée");
      return null;
    }
  }, []);

  // ---- keep listening across visibility changes + re-acquire wake lock when the app returns ----
  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState !== "visible" || !wantListeningRef.current) return;
      requestWakeLock();
      if (!recognitionRef.current) {
        if (restartTimerRef.current) clearTimeout(restartTimerRef.current);
        restartTimerRef.current = setTimeout(() => {
          if (wantListeningRef.current && !recognitionRef.current) startRecognition(onResultRef.current);
        }, 300);
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [requestWakeLock, startRecognition]);

  // ---- text to speech: Gemini → Edge (free neural voice) with browser fallback ----
  // Mobile browsers (esp. iOS Safari) block audio/speech that isn't started from a user
  // gesture. Call this once on the first tap (mic / send) to unlock both engines.
  const audioUnlockedRef = useRef(false);
  const unlockAudio = useCallback(async () => {
    try {
      const ctx = audioCtxRef.current || new (window.AudioContext || window.webkitAudioContext)();
      audioCtxRef.current = ctx;
      if (ctx.state === "suspended") await ctx.resume();
    } catch (_) {}
    if (audioUnlockedRef.current) return;
    audioUnlockedRef.current = true;
    // Prime speechSynthesis with a silent utterance inside the gesture (iOS unlock)
    try {
      if (synth) {
        synth.cancel();
        const u = new SpeechSynthesisUtterance(" ");
        u.volume = 0;
        synth.speak(u);
        synth.resume();
      }
    } catch (_) {}
    // Prime HTMLMediaElement playback with a silent clip
    try {
      const a = new Audio(SILENT_WAV);
      a.volume = 0;
      const p = a.play();
      if (p && p.catch) p.catch(() => {});
    } catch (_) {}
  }, []);

  const browserSpeak = useCallback((text, { onEnd } = {}) => {
    if (!synth || !text || !text.trim()) {
      onEnd && onEnd();
      return;
    }
    const speakNow = () => {
      try { synth.cancel(); } catch (_) {}
      const utter = new SpeechSynthesisUtterance(text);
      const chosen = pickFemaleVoice(synth.getVoices());
      if (chosen) {
        utter.voice = chosen;
        utter.lang = chosen.lang || "fr-FR";
      } else {
        utter.lang = "fr-FR";
      }
      utter.rate = Math.max(0.7, Math.min(1.3, parseFloat(localStorage.getItem("sara_tts_rate")) || 1));
      utter.pitch = 1.12;
      utter.onstart = () => setSpeaking(true);
      utter.onend = () => {
        setSpeaking(false);
        onEnd && onEnd();
      };
      utter.onerror = () => {
        setSpeaking(false);
        onEnd && onEnd();
      };
      try { synth.resume(); } catch (_) {}
      synth.speak(utter);
    };
    // On mobile getVoices() can be empty on first call — wait once for it to populate
    if (!synth.getVoices().length) {
      let done = false;
      const go = () => { if (done) return; done = true; speakNow(); };
      try { synth.addEventListener("voiceschanged", go, { once: true }); } catch (_) {}
      setTimeout(go, 300);
    } else {
      speakNow();
    }
  }, []);

  // ---- shared: wire an <audio> element into the Web Audio analyser (lip-sync) ----
  const attachAnalyser = useCallback(async (audio) => {
    try {
      const ctx = audioCtxRef.current || new (window.AudioContext || window.webkitAudioContext)();
      audioCtxRef.current = ctx;
      if (ctx.state === "suspended") await ctx.resume();
      let source = speakSourceMapRef.current.get(audio);
      if (!source) {
        source = ctx.createMediaElementSource(audio);
        speakSourceMapRef.current.set(audio, source);
      }
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      analyser.smoothingTimeConstant = 0.6;
      source.connect(analyser);
      source.connect(ctx.destination);
      const bins = new Uint8Array(analyser.frequencyBinCount);
      const measure = () => {
        analyser.getByteFrequencyData(bins);
        let sum = 0, weight = 0;
        for (let i = 0; i < bins.length; i++) {
          const w = i < 32 ? 1.4 : i < 96 ? 1 : 0.5;
          sum += bins[i] * w;
          weight += w;
        }
        const avg = sum / weight / 255;
        speakLevelRef.current = Math.max(0, Math.min(1, Math.pow(avg, 0.75) * 1.3));
        speakRafRef.current = requestAnimationFrame(measure);
      };
      measure();
    } catch (_) {
      // playback still works without the analyser (lip-sync falls back to synthetic)
    }
  }, []);

  const clearSpeakRaf = useCallback(() => {
    if (speakRafRef.current) {
      cancelAnimationFrame(speakRafRef.current);
      speakRafRef.current = null;
    }
    speakLevelRef.current = 0;
  }, []);

  // ---- low-latency STREAMING TTS (ElevenLabs via backend proxy, cookie auth) ----
  // Resolves true once playback starts; returns/throws otherwise so the caller falls
  // back to the full /tts/generate path. Audio keeps playing in the background and
  // onended/onerror run `finish`.
  const trySpeakStream = useCallback(async (text, voiceId, onEnd) => {
    const payload = voiceId ? { text, voice_id: voiceId } : { text };
    const { data } = await api.post("/tts/stream/prepare", payload); // throws (409/402) → fallback
    const sid = data && data.stream_id;
    if (!sid) return false;
    const audio = new Audio(`${BACKEND}/api/tts/stream/${sid}`);
    audio.crossOrigin = "anonymous";
    audioElRef.current = audio;
    await attachAnalyser(audio);
    return await new Promise((resolve) => {
      let started = false;
      const finish = () => {
        if (audioElRef.current === audio) audioElRef.current = null;
        clearSpeakRaf();
        setSpeaking(false);
        onEnd && onEnd();
      };
      audio.onplaying = () => {
        if (started) return;
        started = true;
        resolve(true); // streaming works — stay speaking until onended
      };
      audio.onended = finish;
      audio.onerror = () => {
        if (started) return finish();
        // pre-play failure → clean up and let the caller fall back
        if (audioElRef.current === audio) audioElRef.current = null;
        clearSpeakRaf();
        resolve(false);
      };
      const p = audio.play();
      if (p && p.catch) p.catch(() => { if (!started) { clearSpeakRaf(); resolve(false); } });
    });
  }, [attachAnalyser, clearSpeakRaf]);

  const speak = useCallback(
    async (text, voiceId, { onEnd } = {}) => {
      if (!text || !text.trim()) {
        onEnd && onEnd();
        return;
      }
      // stop anything currently playing
      try {
        if (audioElRef.current) {
          audioElRef.current.pause();
          audioElRef.current = null;
        }
      } catch (_) {}
      clearSpeakRaf();
      if (synth) synth.cancel();

      setSpeaking(true);

      // 1) Gemini TTS (Sara's voice) via /tts/generate — browser voice as fallback
      try {
        const payload = voiceId ? { text, voice_id: voiceId } : { text };
        const { data } = await api.post("/tts/generate", payload);
        window.dispatchEvent(new CustomEvent("sara:voice", { detail: { engine: data.engine || "server" } }));
        const audio = new Audio(data.audio);
        audio.crossOrigin = "anonymous";
        audio.playbackRate = Math.max(0.7, Math.min(1.3, parseFloat(localStorage.getItem("sara_tts_rate")) || 1));
        audioElRef.current = audio;
        await attachAnalyser(audio);
        const finish = () => {
          if (audioElRef.current === audio) audioElRef.current = null;
          clearSpeakRaf();
          setSpeaking(false);
          onEnd && onEnd();
        };
        audio.onended = finish;
        audio.onerror = finish;
        await audio.play();
      } catch (e) {
        // Gemini quota reached AND Edge unreachable → browser voice (last resort)
        const detail = e?.response?.data?.detail;
        window.dispatchEvent(new CustomEvent("sara:voice", { detail: { engine: "browser", message: detail } }));
        browserSpeak(text, { onEnd });
      }
    },
    [browserSpeak, attachAnalyser, clearSpeakRaf]
  );

  const stopSpeaking = useCallback(() => {
    try {
      if (audioElRef.current) {
        audioElRef.current.pause();
        audioElRef.current = null;
      }
    } catch (_) {}
    if (speakRafRef.current) {
      cancelAnimationFrame(speakRafRef.current);
      speakRafRef.current = null;
    }
    speakLevelRef.current = 0;
    if (synth) synth.cancel();
    setSpeaking(false);
  }, []);

  // ---- MediaSession: metadata + hardware controls for car head units, TV remotes, lock screens ----
  useEffect(() => {
    if (typeof navigator === "undefined" || !("mediaSession" in navigator)) return;
    try {
      navigator.mediaSession.metadata = new window.MediaMetadata({
        title: "Sara Connor",
        artist: "Assistant IA vocal",
        album: "Sara Connor",
        artwork: [
          { src: "/favicon-sarah.png", sizes: "192x192", type: "image/png" },
          { src: "/favicon-sarah.png", sizes: "512x512", type: "image/png" },
        ],
      });
      navigator.mediaSession.playbackState = speaking ? "playing" : "paused";
      navigator.mediaSession.setActionHandler("pause", () => stopSpeaking());
      navigator.mediaSession.setActionHandler("stop", () => stopSpeaking());
      navigator.mediaSession.setActionHandler("play", () => {});
    } catch (_) {}
  }, [speaking, stopSpeaking]);

  useEffect(
    () => () => {
      wantListeningRef.current = false;
      if (restartTimerRef.current) clearTimeout(restartTimerRef.current);
      if (flushTimerRef.current) clearTimeout(flushTimerRef.current);
      try {
        recognitionRef.current && recognitionRef.current.stop();
      } catch (_) {}
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
      try { wakeLockRef.current && wakeLockRef.current.release && wakeLockRef.current.release(); } catch (_) {}
      wakeLockRef.current = null;
      try {
        if (audioElRef.current) audioElRef.current.pause();
      } catch (_) {}
      if (synth) synth.cancel();
    },
    []
  );

  return {
    voices,
    listening,
    speaking,
    interim,
    micReady,
    micLevelRef,
    speakLevelRef,
    recogSupported,
    recognitionBroken,
    ttsSupported,
    enableMic,
    disableMic,
    startRecognition,
    stopRecognition,
    startListening,
    stopListening,
    setRecogLang,
    setSilenceMs,
    whisperRecording,
    startWhisper,
    stopWhisper,
    speak,
    stopSpeaking,
    unlockAudio,
  };
}
